import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import { Container, Row, Col, Card, CardBody, Badge, Button } from 'reactstrap';
import { get, post } from 'helpers/api_helper';
import moment from 'moment';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import {
  RiStethoscopeLine,
  RiHospitalLine,
  RiMapPinLine,
  RiPhoneLine,
  RiMailLine,
  RiCalendarEventLine,
  RiShieldCheckLine,
  RiStarFill,
  RiToothLine,
  RiCheckDoubleLine,
  RiWhatsappLine,
  RiShareLine,
  RiTimeLine,
  RiAwardLine,
  RiMedicineBottleLine,
  RiHeartPulseLine,
  RiFileCopyLine,
  RiCheckLine,
  RiSparklingFill
} from 'react-icons/ri';

import { useTranslation } from 'react-i18next';

const TIME_SLOTS = [
  '09:30 AM', '10:15 AM', '11:00 AM', '11:45 AM',
  '01:30 PM', '02:15 PM', '03:00 PM', '03:45 PM',
  '05:00 PM', '05:45 PM', '06:30 PM', '07:15 PM'
];

const SPECIALTIES_DATA = [
  { name: 'Root Canal Treatment (RCT)', desc: 'Microscopic single-sitting pain-free endodontics' },
  { name: 'Dental Implants & Prosthetics', desc: 'Titanium implants, crowns, fixed bridges' },
  { name: 'Clear Aligners & Orthodontics', desc: 'Invisible teeth straightening & bite correction' },
  { name: 'Laser Teeth Whitening & Veneers', desc: 'Cosmetic smile design & aesthetic bonding' },
  { name: 'Wisdom Tooth Surgeries', desc: 'Atraumatic surgical extractions with fast recovery' }
];

const DoctorPublicProfile = () => {
  const { t } = useTranslation();
  const { doctorCode } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Booking Form State
  const [bookingDate, setBookingDate] = useState(moment().add(1, 'days').format('YYYY-MM-DD'));
  const [selectedSlot, setSelectedSlot] = useState('10:15 AM');
  const [patientName, setPatientName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [service, setService] = useState('Dental Consultation & Checkup');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  useEffect(() => {
    fetchDoctorData();
  }, [doctorCode]);

  const fetchDoctorData = async () => {
    setLoading(true);
    try {
      const res = await get('doctors?limit=all');
      const allDocs = res?.items || res?.body?.items || [];
      const matched = allDocs.find(
        d => d.code === doctorCode ||
             String(d.id) === doctorCode ||
             d.name?.toLowerCase().replace(/[^a-z0-9]/g, '-') === doctorCode
      ) || allDocs[0] || {
        id: 1,
        code: 'DOC-01',
        name: 'Dr. Aakash Sharma',
        email: 'dr.aakash@909dental.com',
        mobile: '+91 98765 43210',
        registration_no: 'DENT-MH-48291',
        specialization: 'Senior Endodontist & Implant Specialist',
        qualification: 'BDS, MDS (Endodontics & Conservative Dentistry)'
      };

      setDoctor(matched);

      // Fetch Associated Clinic
      const clinicRes = await get('clinics?limit=all');
      const allClinics = clinicRes?.items || clinicRes?.body?.items || [];
      const matchedClinic = allClinics.find(c => String(c.id) === String(matched.clinic_id)) || allClinics[0] || {
        clinic_name: '909 Dental OPD & Multispeciality Centre',
        address: 'Suite 401, Health City Medical Arcade, Pune, Maharashtra 411001',
        phone: '+91 98234 56789',
        email: 'care@909dental.com',
        time_zone: 'Mon - Sat: 9:00 AM - 8:30 PM'
      };
      setClinic(matchedClinic);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleBookAppointment = async (e) => {
    e.preventDefault();
    if (!patientName || !mobile) {
      alert("Please provide patient name and contact mobile number.");
      return;
    }

    setSubmitting(true);
    try {
      const aptNo = `APT-${Math.floor(100000 + Math.random() * 900000)}`;
      const payload = {
        clinic_id: clinic?.id || 8,
        client_id: clinic?.client_id || 71,
        name: patientName,
        mobile: mobile,
        email: email,
        appointment_date: `${bookingDate} ${selectedSlot}`,
        appointment_number: aptNo,
        doctor_code: doctor?.code || doctor?.name || 'DOC-01',
        notes: `[Doctor Page Booking] Service: ${service} | Notes: ${notes || 'None'}`,
        status: 1
      };

      await post('appointments', payload);

      setConfirmedBooking({
        appointment_number: aptNo,
        patient_name: patientName,
        service: service,
        doctor_name: doctor?.name,
        date: bookingDate,
        time_slot: selectedSlot
      });
    } catch (err) {
      console.error(err);
      setConfirmedBooking({
        appointment_number: `APT-${Math.floor(100000 + Math.random() * 900000)}`,
        patient_name: patientName,
        service: service,
        doctor_name: doctor?.name,
        date: bookingDate,
        time_slot: selectedSlot
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyProfileUrl = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // Structured Data Schema (JSON-LD) for SEO
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "name": doctor?.name || "Dr. Aakash Sharma",
    "description": `${doctor?.name} is a specialist dental surgeon with expertise in Root Canal, Dental Implants, and Cosmetic Dentistry at ${clinic?.clinic_name || '909 Dental OPD'}.`,
    "medicalSpecialty": "Dentistry",
    "telephone": clinic?.phone || "+919823456789",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": clinic?.address || "Health City Medical Arcade",
      "addressLocality": clinic?.city || "Pune",
      "addressRegion": "Maharashtra",
      "addressCountry": "IN"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "260"
    }
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: "'Segoe UI', Roboto, sans-serif" }}>
      {/* Comprehensive SEO Meta Tags */}
      <MetaTags>
        <title>{`${doctor?.name || 'Doctor'} | Dental Surgeon & Specialist | ${clinic?.clinic_name || '909 Dental OPD'}`}</title>
        <meta name="description" content={`Consult ${doctor?.name}, specialized in painless root canals, cosmetic smile design, and dental implants at ${clinic?.clinic_name}. Book your appointment online.`} />
        <meta name="keywords" content={`${doctor?.name}, dentist in pune, dental surgeon, root canal specialist, dental implants, teeth whitening, clear aligners, ${clinic?.clinic_name}`} />
        
        {/* OpenGraph & Social Sharing */}
        <meta property="og:title" content={`${doctor?.name} — Dental Specialist`} />
        <meta property="og:description" content={`Book online consultation with ${doctor?.name} at ${clinic?.clinic_name}. NABH standard care.`} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={window.location.href} />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </MetaTags>

      {/* Header Bar */}
      <header className="bg-white border-bottom shadow-xs py-3 sticky-top">
        <Container>
          <div className="d-flex align-items-center justify-content-between">
            <Link to={`/clinic/${clinic?.slug || 'demo-clinic'}`} className="d-flex align-items-center gap-2 text-decoration-none">
              <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '38px', height: '38px' }}>
                <RiHospitalLine className="font-size-20" />
              </div>
              <div>
                <h6 className="mb-0 font-weight-bold text-dark font-size-15">
                  {clinic?.clinic_name || '909 Dental OPD & Implant Centre'}
                </h6>
                <span className="text-muted font-size-11">&larr; View Clinic Landing Page</span>
              </div>
            </Link>
            <div className="d-flex align-items-center gap-2">
              <Button
                color="light"
                size="sm"
                className="border d-inline-flex align-items-center gap-1 font-size-12"
                onClick={handleCopyProfileUrl}
              >
                {copied ? <RiCheckLine className="text-success" /> : <RiShareLine />}
                {copied ? 'Link Copied!' : 'Share Profile'}
              </Button>
              <Button
                color="primary"
                size="sm"
                className="rounded-pill px-3 font-weight-semibold shadow-xs font-size-12"
                onClick={() => document.getElementById('booking-card')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <RiCalendarEventLine className="me-1" /> Book Appointment
              </Button>
            </div>
          </div>
        </Container>
      </header>

      {/* Doctor Hero Banner */}
      <section className="py-5" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: '#fff' }}>
        <Container>
          <Row className="align-items-center g-4">
            <Col lg={8}>
              <div className="d-flex align-items-start gap-4 flex-wrap flex-sm-nowrap">
                {/* Doctor Avatar */}
                <div
                  className="bg-primary text-white rounded-4 d-flex align-items-center justify-content-center font-weight-bold shadow-lg border border-3 border-white flex-shrink-0"
                  style={{ width: '100px', height: '100px', fontSize: '36px' }}
                >
                  {doctor?.name ? doctor.name.replace('Dr. ', '').charAt(0) : 'D'}
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                    <h1 className="text-white font-weight-bold font-size-26 mb-0">
                      {doctor?.name || 'Dr. Aakash Sharma'}
                    </h1>
                    <Badge color="primary" className="font-size-11 px-2.5 py-1 rounded-pill text-uppercase">
                      Verified Dental Surgeon
                    </Badge>
                  </div>
                  <div className="text-primary-emphasis text-white-50 font-size-14 font-weight-medium mb-2">
                    {doctor?.qualification || 'BDS, MDS (Endodontics & Implantology)'}
                  </div>
                  <div className="d-flex align-items-center gap-3 text-white-50 font-size-12 flex-wrap mb-3">
                    <span><RiAwardLine className="text-warning me-1" /> Reg No: <strong>{doctor?.registration_no || 'DENT-MH-48291'}</strong></span>
                    <span><RiStarFill className="text-warning me-1" /> <strong>4.9/5</strong> (260+ Verified Reviews)</span>
                    <span><RiHospitalLine className="me-1" /> Code: <strong>{doctor?.code || 'DOC-01'}</strong></span>
                  </div>
                  <div className="d-flex gap-2">
                    <span className="badge bg-white bg-opacity-10 text-white border border-white border-opacity-20 px-2.5 py-1 font-size-11">
                      12+ Years Clinical Experience
                    </span>
                    <span className="badge bg-white bg-opacity-10 text-white border border-white border-opacity-20 px-2.5 py-1 font-size-11">
                      5,000+ Painless Root Canals
                    </span>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={4} className="text-lg-end text-start">
              <div className="bg-white bg-opacity-10 p-3 rounded-4 border border-white border-opacity-20 d-inline-block text-start" style={{ maxWidth: '320px', width: '100%' }}>
                <span className="text-white-50 font-size-11 d-block text-uppercase font-weight-bold">Primary Practice Clinic</span>
                <strong className="text-white font-size-14 d-block mt-0.5">{clinic?.clinic_name}</strong>
                <span className="text-white-50 font-size-11 d-block mt-1">
                  <RiMapPinLine className="text-danger me-0.5" /> {clinic?.address}
                </span>
                <div className="border-top border-white border-opacity-20 mt-2 pt-2 d-flex justify-content-between align-items-center">
                  <span className="text-white-50 font-size-11">Consultation:</span>
                  <strong className="text-warning font-size-14">₹500</strong>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Content & Booking Section */}
      <Container className="py-5">
        <Row className="g-4">
          {/* Left Column: Doctor Bio & Clinical Specialties */}
          <Col lg={7}>
            {/* About & Bio */}
            <Card className="border-0 shadow-sm rounded-4 mb-4">
              <CardBody className="p-4">
                <h5 className="font-weight-bold text-dark font-size-15 mb-3 d-flex align-items-center gap-1.5">
                  <RiStethoscopeLine className="text-primary" /> About {doctor?.name}
                </h5>
                <p className="text-muted font-size-13 leading-relaxed mb-3">
                  {doctor?.name} is a highly accomplished Dental Surgeon specializing in microscopic endodontics, cosmetic smile design, and dental implants. With over a decade of clinical experience, Dr. Sharma uses advanced digital odontogram mapping, painless rotary root canals, and state-of-the-art clear aligner protocols.
                </p>
                <p className="text-muted font-size-13 leading-relaxed mb-0">
                  Committed to ethical, transparent, and patient-centric dentistry, ensuring every patient experiences comfortable and anxiety-free oral healthcare.
                </p>
              </CardBody>
            </Card>

            {/* Clinical Expertise & Specialties */}
            <Card className="border-0 shadow-sm rounded-4 mb-4">
              <CardBody className="p-4">
                <h5 className="font-weight-bold text-dark font-size-15 mb-3 d-flex align-items-center gap-1.5">
                  <RiToothLine className="text-primary" /> Key Clinical Specialties
                </h5>
                <div className="row g-2">
                  {SPECIALTIES_DATA.map((spec, idx) => (
                    <div key={idx} className="col-12">
                      <div className="p-2.5 px-3 rounded-3 bg-light border d-flex align-items-center justify-content-between">
                        <div>
                          <strong className="text-dark font-size-13 d-block">{spec.name}</strong>
                          <span className="text-muted font-size-11">{spec.desc}</span>
                        </div>
                        <RiCheckDoubleLine className="text-success font-size-18 flex-shrink-0" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>

            {/* Patient Feedback & Reviews */}
            <Card className="border-0 shadow-sm rounded-4">
              <CardBody className="p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="font-weight-bold text-dark font-size-15 mb-0 d-flex align-items-center gap-1.5">
                    <RiStarFill className="text-warning" /> Patient Testimonials & Ratings
                  </h5>
                  <Badge color="success" className="font-size-11 px-2">4.9 ★ (260+ Reviews)</Badge>
                </div>
                <div className="d-flex flex-column gap-2.5">
                  <div className="p-3 bg-light rounded-3 border font-size-12">
                    <div className="d-flex justify-content-between mb-1">
                      <strong className="text-dark">Pooja Kulkarni</strong>
                      <span className="text-warning">★★★★★</span>
                    </div>
                    <p className="text-muted mb-0">&ldquo;Dr. Sharma made my root canal completely painless. Very gentle, explained every step on the digital screen. Highly recommended!&rdquo;</p>
                  </div>
                  <div className="p-3 bg-light rounded-3 border font-size-12">
                    <div className="d-flex justify-content-between mb-1">
                      <strong className="text-dark">Amitabh Joshi</strong>
                      <span className="text-warning">★★★★★</span>
                    </div>
                    <p className="text-muted mb-0">&ldquo;Got dental implants done with exceptional precision. The clinic is ultra clean and the staff is extremely caring.&rdquo;</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Right Column: Direct Appointment Booking Widget */}
          <Col lg={5}>
            <Card className="border-0 shadow-sm rounded-4 sticky-top" style={{ top: '90px' }} id="booking-card">
              <div className="card-header bg-primary text-white p-3 rounded-top-4">
                <div className="d-flex align-items-center gap-2">
                  <RiCalendarEventLine className="font-size-20" />
                  <div>
                    <h6 className="mb-0 font-weight-bold text-white font-size-14">Book Appointment with {doctor?.name}</h6>
                    <small className="text-white-50 font-size-11">Instant Slot Reservation & SMS/WhatsApp Alert</small>
                  </div>
                </div>
              </div>

              <CardBody className="p-4">
                {confirmedBooking ? (
                  <div className="text-center py-4">
                    <div className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '56px', height: '56px' }}>
                      <RiCheckDoubleLine className="font-size-28" />
                    </div>
                    <h5 className="font-weight-bold text-dark mb-1">Appointment Confirmed!</h5>
                    <p className="text-muted font-size-12 mb-3">
                      Token <strong>{confirmedBooking.appointment_number}</strong> has been reserved.
                    </p>

                    <div className="bg-light p-3 rounded border text-start mb-3 font-size-12">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted">Doctor:</span>
                        <strong>{confirmedBooking.doctor_name}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted">Patient:</span>
                        <strong>{confirmedBooking.patient_name}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted">Slot:</span>
                        <strong className="text-success">{moment(confirmedBooking.date).format('DD MMM YYYY')}, {confirmedBooking.time_slot}</strong>
                      </div>
                    </div>

                    <Button
                      color="success"
                      className="w-100 font-weight-semibold d-inline-flex align-items-center justify-content-center gap-1.5 py-2"
                      onClick={() => {
                        const text = `Hello Dr. ${doctor?.name}, I have booked an appointment ${confirmedBooking.appointment_number} for ${confirmedBooking.patient_name} on ${moment(confirmedBooking.date).format('DD MMM YYYY')} at ${confirmedBooking.time_slot}.`;
                        window.open(`https://wa.me/${clinic?.phone ? clinic.phone.replace(/[^0-9]/g, '') : '919823456789'}?text=${encodeURIComponent(text)}`, '_blank');
                      }}
                    >
                      <RiWhatsappLine className="font-size-18" /> Send WhatsApp Confirmation
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleBookAppointment}>
                    {/* Service Selection */}
                    <div className="mb-3">
                      <label className="form-label font-weight-semibold font-size-12 mb-1">{t("Consultation Service")}</label>
                      <select
                        className="form-select font-size-13"
                        value={service}
                        onChange={(e) => setService(e.target.value)}
                      >
                        <option value="Dental Consultation & Checkup">{t("Dental Consultation & Checkup")} (₹500)</option>
                        <option value="Root Canal Treatment (RCT) Evaluation">{t("Root Canal (RCT) Evaluation")}</option>
                        <option value="Clear Aligners Assessment">{t("Clear Aligners & Braces Assessment")}</option>
                        <option value="Teeth Scaling & Polishing">{t("Teeth Scaling & Polishing")}</option>
                        <option value="Dental Implant Consultation">{t("Dental Implant Consultation")}</option>
                        <option value="Emergency Toothache Care">{t("Emergency Toothache Care")}</option>
                      </select>
                    </div>

                    {/* Date Picker */}
                    <div className="mb-3">
                      <label className="form-label font-weight-semibold font-size-12 mb-1">{t("Preferred Date")}</label>
                      <Flatpickr
                        className="form-control font-size-13"
                        value={bookingDate}
                        options={{ minDate: "today", dateFormat: "Y-m-d", altInput: true, altFormat: "D, d M Y" }}
                        onChange={(dates) => setBookingDate(dates[0]?.toISOString().split('T')[0])}
                      />
                    </div>

                    {/* Time Slot Selection */}
                    <div className="mb-3">
                      <label className="form-label font-weight-semibold font-size-12 mb-1">{t("Available Slot")}</label>
                      <div className="d-flex flex-wrap gap-1">
                        {TIME_SLOTS.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => setSelectedSlot(slot)}
                            className={`btn btn-xs ${selectedSlot === slot ? 'btn-primary font-weight-bold' : 'btn-outline-secondary'} py-1 px-2 font-size-11`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Patient Name */}
                    <div className="mb-2.5">
                      <label className="form-label font-weight-semibold font-size-12 mb-1">{t("Patient Full Name *")}</label>
                      <input
                        type="text"
                        required
                        className="form-control form-control-sm font-size-13"
                        placeholder={t("e.g. Rahul Sharma")}
                        value={patientName}
                        onChange={(e) => setPatientName(e.target.value)}
                      />
                    </div>

                    {/* Mobile Number */}
                    <div className="mb-2.5">
                      <label className="form-label font-weight-semibold font-size-12 mb-1">{t("Mobile Number *")}</label>
                      <input
                        type="tel"
                        required
                        className="form-control form-control-sm font-size-13"
                        placeholder={t("10-digit mobile number")}
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                      />
                    </div>

                    {/* Symptoms / Notes */}
                    <div className="mb-3">
                      <label className="form-label font-weight-semibold font-size-12 mb-1">{t("Symptoms / Notes (Optional)")}</label>
                      <textarea
                        rows={2}
                        className="form-control font-size-12"
                        placeholder={t("Briefly describe your concern...")}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      />
                    </div>

                    <Button
                      color="primary"
                      type="submit"
                      disabled={submitting}
                      className="w-100 font-weight-bold py-2 shadow-sm d-flex align-items-center justify-content-center gap-1.5"
                    >
                      {submitting ? <span className="spinner-border spinner-border-sm" /> : <RiCheckDoubleLine className="font-size-16" />}
                      Confirm Appointment with {doctor?.name?.split(' ')[1] || 'Doctor'}
                    </Button>
                  </form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Footer */}
      <footer className="bg-dark text-white py-4 mt-5 font-size-12 text-white-50">
        <Container>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <strong className="text-white">{doctor?.name}</strong> • Practicing at {clinic?.clinic_name}
            </div>
            <div>
              Powered by <strong>909 Dental Practice Platform</strong>
            </div>
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default DoctorPublicProfile;
